import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { MapPin, Clock, Zap, ChevronRight, Mail, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/providers/ThemeProvider";
import { tokens } from "@/lib/theme";
import { JobsContent, t } from "@/content/jobs";
import MiniMarkdown from "@/components/careers/MiniMarkdown";
import JobApplicationForm from "@/components/careers/JobApplicationForm";
import ProviderCTA from "@/components/careers/ProviderCTA";
import { Seo } from "@/components/seo/Seo";
import { type } from "@/lib/typography";
import { PublicShell } from "@/components/layouts/PublicShell";

function adaptJob(row) {
  return {
    id: row.id, slug: row.slug, kind: row.kind,
    number: row.sort_order ? String(row.sort_order).padStart(2, "0") : "00",
    department: {
      en: row.department_label_en || row.department,
      fr: row.department_label_fr || row.department_label_en || row.department,
      es: row.department_label_en || row.department,
      pt: row.department_label_en || row.department,
    },
    title: {
      en: row.title_en,
      fr: row.title_fr || row.title_en,
      es: row.title_es || row.title_en,
      pt: row.title_pt || row.title_en,
    },
    location: {
      en: row.location_label_en,
      fr: row.location_label_fr || row.location_label_en,
      es: row.location_label_es || row.location_label_en,
      pt: row.location_label_pt || row.location_label_en,
    },
    body_md: {
      en: row.body_md_en,
      fr: row.body_md_fr || row.body_md_en,
      es: row.body_md_es || row.body_md_en,
      pt: row.body_md_pt || row.body_md_en,
    },
    external_apply_url: row.external_apply_url
  };
}

const PROVIDER_BODY = {
  who_h: { en: "Who this is for", fr: "Pour qui ce rôle", es: "Para quién es este rol", pt: "Para quem é este papel" },
  who: [
    { en: "Operators with 5–15 years in founder-facing roles: strategy, fundraising, growth, PMF, fractional leadership",
      fr: "Opérateur·rice·s avec 5–15 ans dans des rôles proches fondateurs : stratégie, levée, croissance, PMF, leadership fractionné",
      es: "Operadores con 5–15 años en roles cercanos a fundadores: estrategia, fundraising, crecimiento, PMF, liderazgo fraccional",
      pt: "Operadores com 5–15 anos em papéis próximos a fundadores: estratégia, captação, crescimento, PMF, liderança fracionada" },
    { en: "Fluent in English or French. Spanish or Portuguese a strong bonus.",
      fr: "Courant·e en anglais ou français. Espagnol ou portugais un bonus fort.",
      es: "Fluidez en inglés o francés. Español o portugués un gran plus.",
      pt: "Fluência em inglês ou francês. Espanhol ou português um grande diferencial." },
    { en: "Based anywhere globally, EMEA or LATAM business hours",
      fr: "Basé·e n'importe où, heures business EMEA ou LATAM",
      es: "Ubicado en cualquier parte, horario EMEA o LATAM",
      pt: "Baseado em qualquer lugar, horário EMEA ou LATAM" }
  ],
  get_h: { en: "What you get on the platform", fr: "Ce que vous obtenez sur la plateforme", es: "Lo que obtienes en la plataforma", pt: "O que você recebe na plataforma" },
  get: [
    { en: "Profile reaching founders across five emerging-market cities",
      fr: "Profil atteignant les fondateurs dans cinq villes des marchés émergents",
      es: "Perfil que llega a fundadores en cinco ciudades de mercados emergentes",
      pt: "Perfil que alcança fundadores em cinco cidades de mercados emergentes" },
    { en: "85% of every booking, paid within 7 days via Stripe Connect",
      fr: "85 % de chaque booking, payés sous 7 jours via Stripe Connect",
      es: "85 % de cada reserva, pagado en 7 días vía Stripe Connect",
      pt: "85% de cada reserva, pago em 7 dias via Stripe Connect" },
    { en: "Set your own rates, availability, and working hours",
      fr: "Fixez vos tarifs, vos disponibilités, vos heures",
      es: "Define tus tarifas, disponibilidad y horarios",
      pt: "Defina suas tarifas, disponibilidade e horários" },
    { en: "Calendar, booking, and payment infrastructure handled by us",
      fr: "Calendrier, booking et infrastructure de paiement gérés par nous",
      es: "Calendario, reservas e infraestructura de pago gestionados por nosotros",
      pt: "Calendário, reservas e infraestrutura de pagamento gerenciados por nós" },
    { en: "Founder context delivered before every call — you walk in ready",
      fr: "Contexte fondateur livré avant chaque appel — vous arrivez préparé·e",
      es: "Contexto del fundador antes de cada llamada — llegas preparado",
      pt: "Contexto do fundador antes de cada chamada — você chega preparado" }
  ],
  expect_h: { en: "What Bootpreneurs expects from you", fr: "Ce que Bootpreneurs attend de vous", es: "Lo que Bootpreneurs espera de ti", pt: "O que a Bootpreneurs espera de você" },
  expect: [
    { en: "Respond to new booking requests within 24 hours", fr: "Répondre aux nouvelles demandes sous 24h", es: "Responde a nuevas solicitudes en 24 horas", pt: "Responder a novas solicitações em 24h" },
    { en: "Show up on time, every time. 48h+ notice for cancellations", fr: "Être à l'heure, à chaque fois. 48h+ de préavis pour annuler", es: "Llega puntual siempre. 48h+ de aviso para cancelar", pt: "Seja pontual sempre. 48h+ de aviso para cancelar" },
    { en: "Keep ratings above 4.5 / 5", fr: "Garder des notes au-dessus de 4,5 / 5", es: "Mantén calificaciones sobre 4,5 / 5", pt: "Manter avaliações acima de 4,5 / 5" },
    { en: "Keep bio, expertise tags, availability current", fr: "Garder bio, tags d'expertise, disponibilités à jour", es: "Mantén bio, etiquetas y disponibilidad al día", pt: "Mantenha bio, tags e disponibilidade atualizadas" }
  ],
  how_h: { en: "How it works", fr: "Comment ça marche", es: "Cómo funciona", pt: "Como funciona" },
  how: [
    { en: "Apply via the provider form (7 steps, ~15 minutes)", fr: "Candidater via le formulaire provider (7 étapes, environ 15 min)", es: "Postula vía el formulario provider (7 pasos, ~15 min)", pt: "Candidate-se pelo formulário provider (7 etapas, ~15 min)" },
    { en: "Approved within 7 days after review", fr: "Approuvé·e sous 7 jours après examen", es: "Aprobado en 7 días tras revisión", pt: "Aprovado em 7 dias após análise" },
    { en: "Start earning on your first booking", fr: "Commencer à gagner dès le premier booking", es: "Comienza a ganar desde tu primera reserva", pt: "Comece a ganhar desde a primeira reserva" }
  ]
};

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n, t: tr } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const themeCtx = useTheme?.();
  const resolvedTheme = (themeCtx?.theme === "dark" || (themeCtx?.theme === "system" && typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches)) ? "dark" : "light";
  const T = useMemo(() => tokens(resolvedTheme), [resolvedTheme]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    supabase.from("job_postings").select("*").eq("slug", slug).maybeSingle()
      .then(({ data }) => {
        setJob(data ? adaptJob(data) : null);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <PublicShell><div style={{ padding: 40, color: T.textMute }}>{tr("careers.detail.loading")}</div></PublicShell>;
  if (!job) return <PublicShell><div style={{ padding: 40, color: T.textMute }}>{tr("careers.detail.notFound")}</div></PublicShell>;

  const onBack = () => navigate("/careers");
  const isProvider = job.kind === "provider";
  const titlePlain = t(job.title, lang).replace(/\n/g, " ");

  return (
    <PublicShell>
      <Seo
        title={tr("careers.detail.seoTitle", { title: titlePlain })}
        description={tr("careers.detail.seoDescription", { title: titlePlain })}
      />
      <div style={{
        color: T.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px clamp(20px, 4vw, 48px) 0" }}>
          <nav style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: T.textFaint }}>
            <button onClick={onBack} style={{ background: "none", border: "none", color: T.textMute, cursor: "pointer", padding: 0, fontSize: 13 }}>
              {tr("careers.detail.breadcrumb")}
            </button>
            <ChevronRight size={13} />
            <span style={{ color: T.text }}>{titlePlain}</span>
          </nav>
        </div>

        <section style={{ maxWidth: 1280, margin: "0 auto", padding: "clamp(40px, 6vw, 80px) clamp(20px, 4vw, 48px) clamp(40px, 6vw, 60px)" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
            {isProvider ? (
              <>
                <span style={{
                  display: "inline-flex", alignItems: "center", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: T.bgSubtle, color: T.textMute, fontFamily: "ui-monospace, Menlo, monospace",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>{t(JobsContent.ui.marketplace_role, lang)}</span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                  backgroundColor: T.amberSoft, color: T.amber, fontFamily: "ui-monospace, Menlo, monospace",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>
                  <Zap size={10} strokeWidth={2.5} />
                  {t(JobsContent.ui.commission_badge, lang)}
                </span>
              </>
            ) : (
              <>
                <span style={{
                  display: "inline-flex", alignItems: "center", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: T.bgSubtle, color: T.textMute, fontFamily: "ui-monospace, Menlo, monospace",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>{job.number} · {t(job.department, lang)}</span>
                <span style={{
                  display: "inline-flex", alignItems: "center", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: T.primarySoft, color: T.primary, fontFamily: "ui-monospace, Menlo, monospace",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>{t(JobsContent.ui.founding, lang)}</span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: T.dangerSoft, color: T.danger, fontFamily: "ui-monospace, Menlo, monospace",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: T.danger }} />
                  {t(JobsContent.ui.hiring_now, lang)}
                </span>
              </>
            )}
          </div>
          <h1 style={{
            ...type.displayMd,
            color: T.text, marginTop: 0, marginBottom: 28, whiteSpace: "pre-line"
          }}>{t(job.title, lang)}</h1>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, ...type.bodySm, color: T.textMute }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <MapPin size={15} strokeWidth={2} color={T.textFaint} />
              {t(job.location, lang)}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Clock size={15} strokeWidth={2} color={T.textFaint} />
              {isProvider ? tr("careers.detail.providerHours") : tr("careers.detail.fullTimeRemote")}
            </div>
          </div>
        </section>

        {isProvider ? (
          <section style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px) clamp(80px, 12vh, 140px)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr min(360px, 32%)", gap: 48 }} className="bp-detail-grid">
              <article>
                {[
                  { h: PROVIDER_BODY.who_h, items: PROVIDER_BODY.who, accent: T.primary },
                  { h: PROVIDER_BODY.get_h, items: PROVIDER_BODY.get, accent: T.amber },
                  { h: PROVIDER_BODY.expect_h, items: PROVIDER_BODY.expect, accent: T.textMute }
                ].map((section, si) => (
                  <div key={si} style={{ marginBottom: 40 }}>
                    <h2 style={{ ...type.displaySm, color: T.text, marginTop: 0, marginBottom: 16 }}>
                      {t(section.h, lang)}
                    </h2>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                      {section.items.map((item, i) => (
                        <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, color: T.textMute, lineHeight: 1.6 }}>
                          <CheckCircle2 size={18} color={section.accent} strokeWidth={2} style={{ flexShrink: 0, marginTop: 3 }} />
                          <span>{t(item, lang)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div>
                  <h2 style={{ ...type.displaySm, color: T.text, marginTop: 0, marginBottom: 20 }}>
                    {t(PROVIDER_BODY.how_h, lang)}
                  </h2>
                  <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                    {PROVIDER_BODY.how.map((item, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14, color: T.textMute, lineHeight: 1.6 }}>
                        <span style={{
                          flexShrink: 0, width: 28, height: 28, borderRadius: "50%", backgroundColor: T.primary,
                          color: "#FFFFFF", fontSize: 13, fontWeight: 700, display: "flex",
                          alignItems: "center", justifyContent: "center", fontFamily: "ui-monospace, Menlo, monospace"
                        }}>{i + 1}</span>
                        <span style={{ paddingTop: 3 }}>{t(item, lang)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </article>
              <aside>
                <div style={{ position: "sticky", top: 96 }}>
                  <ProviderCTA job={job} lang={lang} T={T} />
                </div>
              </aside>
            </div>
          </section>
        ) : (
          <>
            <section style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px) clamp(60px, 10vh, 100px)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr min(320px, 30%)", gap: 48 }} className="bp-detail-grid">
                <article><MiniMarkdown source={t(job.body_md, lang)} T={T} /></article>
                <aside>
                  <div style={{ position: "sticky", top: 96 }}>
                    <div style={{ borderRadius: 20, padding: 24, backgroundColor: T.bgElev, border: `1px solid ${T.border}` }}>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginTop: 0, marginBottom: 16 }}>
                        {t(JobsContent.ui.apply_now, lang)}
                      </h3>
                      <button onClick={() => {
                        const el = document.getElementById("apply");
                        if (el) {
                          const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
                          window.scrollTo({ top: y, behavior: "smooth" });
                        }
                      }} style={{
                        display: "block", width: "100%", textAlign: "center", padding: "12px 16px", borderRadius: 10,
                        backgroundColor: T.primary, color: "#FFFFFF", fontWeight: 600, border: "none", cursor: "pointer", fontSize: 14
                      }}>
                        {t(JobsContent.ui.apply_now, lang)} →
                      </button>
                      <div style={{ fontSize: 12, color: T.textFaint, marginTop: 16, lineHeight: 1.7 }}>
                        {tr("careers.detail.applyTimeline")}
                      </div>
                      <div style={{ fontSize: 12, color: T.textFaint, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.borderSubtle}` }}>
                        {tr("careers.detail.questions")}
                        <a href="mailto:careers@bootpreneurs.com" style={{
                          display: "flex", alignItems: "center", gap: 4, marginTop: 4,
                          color: T.primary, textDecoration: "none", fontWeight: 500
                        }}>
                          <Mail size={11} /> careers@bootpreneurs.com
                        </a>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </section>
            <section id="apply" style={{
              maxWidth: 880, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px) clamp(80px, 12vh, 140px)", scrollMarginTop: 96
            }}>
              <JobApplicationForm job={job} lang={lang} T={T} onBack={onBack} />
            </section>
          </>
        )}
      </div>
    </PublicShell>
  );
}
